"use client";

import { useCallback, useMemo } from 'react';
import { useMediaStore } from '@/shared/store/voice-chat';
import type {
  MediaState,
  MediaStateSaveReason,
  MediaRestorationOptions,
  UseMediaControllerReturn
} from '../types/media-state';
import { MEDIA_CONFIG } from '../const/media-config';
import {
  saveMediaState,
  restoreMediaState,
  clearSavedMediaState,
  hasSavedMediaState,
  getSavedMediaState,
} from './media-restoration';

/**
 * 미디어 상태 컨트롤러 훅
 *
 * 책임:
 * - 마이크/스피커 상태 관리
 * - 상태 저장 및 복원
 * - 모드 전환 시 상태 보존
 */
export const useMediaController = (): UseMediaControllerReturn => {
  // 스토어에서 상태 가져오기
  const isMuted = useMediaStore((state) => state.isMuted);
  const isSpeakerOn = useMediaStore((state) => state.isSpeakerOn);
  const setIsMuted = useMediaStore((state) => state.setIsMuted);
  const setIsSpeakerOn = useMediaStore((state) => state.setIsSpeakerOn);

  // isMuted를 isMicOn으로 변환 (반전)
  const isMicOn = !isMuted;

  /**
   * 마이크 토글
   */
  const toggleMic = useCallback(() => {
    const newMicOn = !isMicOn;
    setIsMuted(!newMicOn);

    if (MEDIA_CONFIG.ENABLE_LOGGING) {
      console.log(`🎤 [MEDIA] 마이크 ${newMicOn ? 'ON' : 'OFF'}`);
    }
  }, [isMicOn, setIsMuted]);

  /**
   * 스피커 토글
   */
  const toggleSpeaker = useCallback(() => {
    const newSpeakerOn = !isSpeakerOn;
    setIsSpeakerOn(newSpeakerOn);

    if (MEDIA_CONFIG.ENABLE_LOGGING) {
      console.log(`🔊 [MEDIA] 스피커 ${newSpeakerOn ? 'ON' : 'OFF'}`);
    }
  }, [isSpeakerOn, setIsSpeakerOn]);

  /**
   * 마이크 상태 설정
   */
  const setMicOn = useCallback((on: boolean) => {
    setIsMuted(!on);

    if (MEDIA_CONFIG.ENABLE_LOGGING) {
      console.log(`🎤 [MEDIA] 마이크 설정: ${on ? 'ON' : 'OFF'}`);
    }
  }, [setIsMuted]);

  /**
   * 스피커 상태 설정
   */
  const setSpeakerOn = useCallback((on: boolean) => {
    setIsSpeakerOn(on);

    if (MEDIA_CONFIG.ENABLE_LOGGING) {
      console.log(`🔊 [MEDIA] 스피커 설정: ${on ? 'ON' : 'OFF'}`);
    }
  }, [setIsSpeakerOn]);

  /**
   * 현재 상태 저장
   */
  const saveCurrentState = useCallback((reason: MediaStateSaveReason = 'manual') => {
    const currentState: MediaState = {
      isMicOn,
      isSpeakerOn,
    };
    saveMediaState(currentState, reason);
  }, [isMicOn, isSpeakerOn]);

  /**
   * 저장된 상태 복원
   */
  const restoreSavedState = useCallback((options: MediaRestorationOptions = {}): boolean => {
    const {
      restoreMic = true,
      restoreSpeaker = true,
      onRestored,
    } = options;

    const saved = getSavedMediaState();
    if (!saved) {
      if (MEDIA_CONFIG.ENABLE_LOGGING) {
        console.log('⚠️ [MEDIA] 복원할 상태 없음');
      }
      return false;
    }

    // 선택적 복원
    if (restoreMic) {
      setIsMuted(!saved.state.isMicOn);
    }
    if (restoreSpeaker) {
      setIsSpeakerOn(saved.state.isSpeakerOn);
    }

    // 복원 후 삭제
    clearSavedMediaState();

    if (MEDIA_CONFIG.ENABLE_LOGGING) {
      console.log('✅ [MEDIA] 상태 복원 완료:', saved.state);
    }

    // 콜백 호출
    onRestored?.(saved.state);

    return true;
  }, [setIsMuted, setIsSpeakerOn]);

  /**
   * 저장된 상태 삭제
   */
  const clearSavedStateHandler = useCallback(() => {
    clearSavedMediaState();
  }, []);

  return {
    // 현재 상태
    isMicOn,
    isSpeakerOn,

    // 기본 제어
    toggleMic,
    toggleSpeaker,
    setMicOn,
    setSpeakerOn,

    // 상태 저장/복원
    saveCurrentState,
    restoreSavedState,
    clearSavedState: clearSavedStateHandler,
    hasSavedState: hasSavedMediaState(),
  };
};
